import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useQueryClient } from '@tanstack/react-query';
// HeroUI Modal components
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button as HeroButton } from '@heroui/react';
// Lucide React 직접 import
import Send from 'lucide-react/dist/esm/icons/send';
import XCircle from 'lucide-react/dist/esm/icons/x-circle';
import toast from 'react-hot-toast';
// API 관련 import
import { AxiosError } from 'axios';
import { useCreateReview, getGetCourseDetailQueryKey, getGetCourseReviewsQueryKey } from '@edusense/course-client';
const FormGroup = styled.div(({ theme })=>({
        marginBottom: theme.spacing.lg
    }));
const Label = styled.label(({ theme })=>({
        display: 'block',
        marginBottom: theme.spacing.sm,
        fontWeight: theme.typography.fontWeight.medium,
        color: theme.textSecondary,
        fontSize: theme.typography.fontSize.sm
    }));
const RatingContainer = styled.div(({ theme })=>({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.xs,
        '.star-icon': {
            cursor: 'pointer',
            fontSize: '1.8rem',
            transition: 'color 0.2s',
            color: theme.textSecondary,
            '&.active': {
                color: theme.warning
            },
            '&:hover': {
            }
        },
        '.rating-text': {
            marginLeft: theme.spacing.sm,
            fontSize: theme.typography.fontSize.sm,
            color: theme.textSecondary
        },
        [`@media (max-width: ${theme.breakpoints.sm})`]: {
            '.star-icon': {
                fontSize: '1.6rem'
            }
        }
    }));
const TextArea = styled.textarea(({ theme })=>({
        width: '100%',
        minHeight: '120px',
        padding: theme.spacing.md,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius.md,
        fontSize: theme.typography.fontSize.base,
        backgroundColor: theme.background,
        color: theme.text,
        resize: 'vertical',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxSizing: 'border-box',
        '&:focus': {
            outline: 'none',
            borderColor: theme.primary,
            boxShadow: `0 0 0 2px ${theme.primary}33`
        },
        '&::placeholder': {
            color: theme.textSecondary
        },
        '&:disabled': {
            backgroundColor: theme.paper,
            cursor: 'not-allowed'
        }
    }));
const CourseReviewModal = ({ courseId, onClose, onSubmitSuccess })=>{
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const { mutateAsync: createReview, isPending: isSubmitting } = useCreateReview({
        mutation: {
            onSuccess: ()=>{
                toast.success('리뷰가 성공적으로 등록되었습니다.');
                // 코스 상세 정보 및 리뷰 목록 갱신
                queryClient.invalidateQueries({
                    queryKey: getGetCourseDetailQueryKey(courseId)
                });
                queryClient.invalidateQueries({
                    queryKey: getGetCourseReviewsQueryKey(courseId, {
                        page: '0',
                        size: '10'
                    })
                });
                onSubmitSuccess();
                onClose();
            }
        }
    });
    const handleRatingChange = (newRating)=>{
        setRating(newRating);
    };
    const handleSubmit = useCallback(async (e)=>{
        e.preventDefault();
        if (rating === 0 || !comment.trim()) {
            toast.error('평점과 리뷰 내용을 모두 입력해주세요.');
            return;
        }
        try {
            console.log(`Submitting review for course ${courseId}: Rating=${rating}, Comment=${comment}`);
            await createReview({
                courseId,
                data: {
                    content: comment.trim(),
                    rating: rating
                }
            });
        } catch (error) {
            console.error('Failed to submit review:', error);
            // Axios 에러 처리 추가
            if (error instanceof AxiosError && error.response?.status === 409) {
                // 409 에러: 이미 리뷰 작성됨
                toast.error('이미 리뷰를 작성했습니다.');
                onClose(); // 모달 닫기
            } else {
                // 그 외 다른 에러
                toast.error('리뷰 등록 중 오류가 발생했습니다.');
            }
        }
    }, [
        courseId,
        rating,
        comment,
        createReview,
        onSubmitSuccess,
        onClose
    ]);
    // HeroUI Modal 사용
    return /*#__PURE__*/ _jsx(Modal, {
        isOpen: true,
        onClose: onClose,
        size: "2xl",
        scrollBehavior: "inside",
        classNames: {
            wrapper: "z-[9999]",
            backdrop: "z-[9998]"
        },
        children: /*#__PURE__*/ _jsx(ModalContent, {
            children: (onClose)=>/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(ModalHeader, {
                            className: "flex flex-col gap-1",
                            children: "강의 리뷰 작성"
                        }),
                        /*#__PURE__*/ _jsx(ModalBody, {
                            children: /*#__PURE__*/ _jsxs("form", {
                                id: "review-form",
                                onSubmit: handleSubmit,
                                children: [
                                    /*#__PURE__*/ _jsxs(FormGroup, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                htmlFor: "rating",
                                                children: "평점"
                                            }),
                                            /*#__PURE__*/ _jsxs(RatingContainer, {
                                                id: "rating",
                                                children: [
                                                    [
                                                        1,
                                                        2,
                                                        3,
                                                        4,
                                                        5
                                                    ].map((star)=>/*#__PURE__*/ _jsx("span", {
                                                            onClick: ()=>handleRatingChange(star),
                                                            className: `star-icon ${star <= rating ? 'active' : ''}`,
                                                            role: "button",
                                                            "aria-label": `별점 ${star}점`,
                                                            children: "★"
                                                        }, star)),
                                                    rating > 0 && /*#__PURE__*/ _jsxs("span", {
                                                        className: "rating-text",
                                                        children: [
                                                            "(",
                                                            rating,
                                                            "점)"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(FormGroup, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                htmlFor: "comment",
                                                children: "리뷰 내용"
                                            }),
                                            /*#__PURE__*/ _jsx(TextArea, {
                                                id: "comment",
                                                value: comment,
                                                onChange: (e)=>setComment(e.target.value),
                                                placeholder: "강의에 대한 솔직한 리뷰를 남겨주세요.",
                                                required: true,
                                                disabled: isSubmitting
                                            })
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs(ModalFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(HeroButton, {
                                    color: "default",
                                    variant: "flat",
                                    onPress: onClose,
                                    isDisabled: isSubmitting,
                                    startContent: /*#__PURE__*/ _jsx(XCircle, {
                                        size: 16
                                    }),
                                    children: "취소"
                                }),
                                /*#__PURE__*/ _jsx(HeroButton, {
                                    color: "primary",
                                    type: "submit",
                                    form: "review-form",
                                    isDisabled: isSubmitting || rating === 0 || !comment.trim(),
                                    isLoading: isSubmitting,
                                    startContent: !isSubmitting ? /*#__PURE__*/ _jsx(Send, {
                                        size: 16
                                    }) : undefined,
                                    children: isSubmitting ? '제출 중...' : '리뷰 제출'
                                })
                            ]
                        })
                    ]
                })
        })
    });
};
// Default export for backward compatibility
export default CourseReviewModal;
